<template>
  <section class="org-intro">
    <div class="org-intro__card">
      <div class="org-intro__gallery">
        <EntityGallery :images="gallery" :alt="detail.title" :height="280" />
      </div>

      <div class="org-intro__content">
        <h2 class="org-intro__title">{{ detail.title }}</h2>
        <dl v-if="fields.length" class="org-intro__meta">
          <div
            v-for="field in fields"
            :key="field.label"
            class="org-intro__meta-row"
          >
            <dt>{{ field.label }}：</dt>
            <dd>
              <template v-if="isSourceField(field) && sourceLinks(field).length">
                <template v-for="(link, index) in sourceLinks(field)">
                  <router-link
                    v-if="link.resourceId"
                    :key="`${field.label}-${link.resourceId}-${index}`"
                    :to="`/resources/${link.resourceId}`"
                    class="org-intro__source-link"
                  >
                    {{ link.text }}
                  </router-link>
                  <span v-else :key="`${field.label}-text-${index}`">{{ link.text }}</span>
                  <span
                    v-if="index < sourceLinks(field).length - 1"
                    :key="`${field.label}-sep-${index}`"
                  >、</span>
                </template>
              </template>
              <template v-else>{{ field.value }}</template>
            </dd>
          </div>
        </dl>
        <div class="org-intro__desc-block">
          <h4 class="org-intro__desc-title">{{ detail.introLabel || '机构介绍' }}</h4>
          <p class="org-intro__desc">{{ organizationDescription.value }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { fetchEntityRelatedResources } from '@/api/knowledge'
import {
  filterEventIntroFields,
  isSourceField,
  resolveSourceLinks
} from '@/utils/entityMeta'
import EntityGallery from './EntityGallery.vue'

export default {
  name: 'OrgIntro',
  components: { EntityGallery },
  props: {
    detail: { type: Object, required: true }
  },
  data() {
    return {
      relatedResources: []
    }
  },
  computed: {
    gallery() {
      return this.detail.gallery || []
    },
    fields() {
      const raw = (this.detail.fields || []).filter((field) => field.code !== 'organizationDescription')
      return filterEventIntroFields(raw)
    },
    organizationDescription() {
      return this.detail.fields.find(field => field.code === 'organizationDescription') || { value: '' }
    }
  },
  watch: {
    'detail.id': {
      immediate: true,
      handler(id) {
        this.loadRelatedResources(id)
      }
    }
  },
  methods: {
    isSourceField,
    sourceLinks(field) {
      return resolveSourceLinks(field, this.relatedResources)
    },
    async loadRelatedResources(entityId) {
      if (!entityId) {
        this.relatedResources = []
        return
      }
      try {
        const { records } = await fetchEntityRelatedResources({
          entityId,
          page: 1,
          limit: 50
        })
        this.relatedResources = records || []
      } catch (error) {
        this.relatedResources = []
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.org-intro {
  margin-bottom: $spacing-3xl;

  &__card {
    display: flex;
    gap: $spacing-xl;
    padding: $spacing-xl;
    background: $color-white;
    border-radius: 4px;
    box-shadow: 0 4px 24px rgba($color-brown-dark, 0.08);
  }

  &__gallery {
    width: 42%;
    flex-shrink: 0;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-family: $font-serif;
    font-size: rem(28);
    font-weight: 700;
    color: $color-brown-dark;
    margin-bottom: $spacing-base;
  }

  &__meta {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: $spacing-xs $spacing-xl;
    margin-bottom: $spacing-base;
    padding-bottom: $spacing-base;
    border-bottom: 1px solid rgba($color-border, 0.5);
  }

  &__meta-row {
    display: flex;
    font-size: rem(14);
    line-height: 1.8;

    dt { color: $color-text-light; flex-shrink: 0; }
    dd { color: $color-text; }
  }

  &__source-link {
    color: $success-color;

    &:hover {
      text-decoration: underline;
    }
  }

  &__desc-title {
    font-size: rem(15);
    font-weight: 600;
    color: $color-brown-dark;
    margin-bottom: $spacing-sm;
  }

  &__desc {
    font-size: rem(14);
    line-height: 1.9;
    color: $color-text-light;
  }
}

@media (max-width: 960px) {
  .org-intro__card {
    flex-direction: column;

    .org-intro__gallery { width: 100%; }
    .org-intro__meta { grid-template-columns: 1fr; }
  }
}
</style>
